// @flow
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import classNames from 'classnames';
import { Map as ImmutableMap } from 'immutable';
import isAcme from 'isAcme';
import { withRouter } from 'react-router-dom';

import { DateTime } from 'luxon';
import { CSSTransition } from 'react-css-transition';

import { getLogger } from 'utils/logger';
import { isOldEdge } from 'utils/utils';

import AmountField from 'components/QuickenControls/AmountField';
import QTip from 'components/QuickenControls/QTip';

import Collapse from '@material-ui/core/Collapse';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import CircularProgress from '@material-ui/core/CircularProgress';

import { withStyles } from '@material-ui/core/styles';

import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ArrowDown from '@material-ui/icons/ArrowDropDown';
import ArrowUp from '@material-ui/icons/ArrowDropUp';
import PriorityHigh from '@material-ui/icons/PriorityHigh';

import QIconButton from 'components/QIconButton';

import { createErrorDialog } from 'data/aggErrors/actions';
import { getQcsAggregationError } from 'data/aggErrors/errors';
import {
  getInstitutionLoginsById,
  getIsRefreshingAll as getIsAllInstitutionLoginsRefreshing,
  getIdsRefreshing as getInstitutionLoginIdsRefreshing,
} from 'data/institutionLogins/selectors';

import * as preferencesSelectors from 'data/preferences/selectors';

import { getFeatureFlags } from 'utils/featureFlags/selectors';
import tracker from 'utils/tracker';

import type { DisplayOptions } from '../types';
import { PrimaryTextLine, SecondaryTextLine, styles } from './styles';

const isIe = require('is-iexplorer') || isOldEdge();  // eslint-disable-line

const log = getLogger('components/AccountList/AccountRow/index.js');

const MAX_LEVEL_TO_EXPAND_COLLAPSE = 2;

CSSTransition.childContextTypes = {
  // child context keys
};

type Props = {
  id: string,
  node: Object,
  displayOptions: ?DisplayOptions,
  dense: boolean,
  omitCents: boolean,
  showColors: boolean,
  balanceType: string,

  onClick: any,
  onExpandCollapse: any,

  selectedNodeId: any,

  institutionLoginsById: any,

  isAllInstitutionLoginsRefreshing: boolean,
  institutionLoginsIdsRefreshing: Set<string>,

  accountColorsById: any,
  featureFlags: ImmutableMap<string, any>,
  dispatch: (any) => void,

  theme: Object,

  classes: Object,

  history: Object,
};

class AccountRow extends React.PureComponent<Props> {

  getBalance = (node) => {
    switch (this.props.balanceType) {
      case 'online':
        return node.sumOnlineAccountBalances;
      case 'today':
        return node.sumCurrentAccountBalances;
      case 'final':
        return node.sumFinalAccountBalances;
      default:
        return node.sumEndingAccountBalances;
    }
  };

  handleErrorIconClick = (error) => {
    const institutionId = error.institutionLogin && error.institutionLogin.institutionId
      ? error.institutionLogin.institutionId
      : '0';
    if (error.showConfirmationDialog) {
      tracker.track(tracker.events.fiLoginFixItFlowStart,
        {
          institution_id: institutionId,
          agg_status: error.status,
          agg_status_code: error.statusCode || 'null',
          location: 'account list',
          cancellable: true,
        });
      this.props.dispatch(createErrorDialog(error));
    } else if (error.action) {
      tracker.track(tracker.events.fiLoginFixItFlowStart,
        {
          institution_id: institutionId,
          agg_status: error.status,
          agg_status_code: error.statusCode || null,
          location: 'account list',
        });
      this.props.dispatch(error.action);
    }
  };

  renderPrimaryText = (node, classes, omitCents, selClass, iconSpace) => {
    let depth = node.depth === 2 && node.isLeaf === true ? 3 : node.depth;
    const balance = this.getBalance(node);
    const negClass = this.getBalance(node) < 0 ? classes.negativeBalDark : classes.positiveBalDark;

    if (node.depth === 1 && node.displayOrder === 0) {
      depth = 0;
    }
    const levelClass = classes[`listItemTextL${depth}`];

    return (
      <PrimaryTextLine>
        <span
          style={{ width: '100%' }}
          className={classNames(levelClass, 'left', selClass)}
        >
          <QTip
            title={node.displayLabel}
            wrapOnly
          >
            <div
              style={{ width: '100%' }}
              className={classNames(levelClass, 'left', selClass)}
            >
              {`${node.displayLabel}`}
              {iconSpace}
            </div>
          </QTip>
        </span>
        <AmountField
          id={`${this.props.id}-balance`}
          value={balance}
          classes={{ inputField: classNames(levelClass, selClass, negClass) }}
          omitCents={omitCents}
          currencySymbol={node.currency}
        />
      </PrimaryTextLine>
    );
  };

  renderSecondaryText = (node, institutionLogin) => {

    log.log('Rendering secondary text');
    if (!node.account) {
      // secondary text line only exists when we have an account
      return null;
    }

    let lastSuccessfulRefreshOn = '';
    if (node.account.aggregators && node.account.aggregators.size > 0) {
      const aggregator = node.account.aggregators.get(0);
      if (aggregator.lastRefreshSuccessfulAt) {
        const lastRefreshSuccessAt = DateTime.fromISO(aggregator.lastRefreshSuccessfulAt);
        lastSuccessfulRefreshOn = lastRefreshSuccessAt.toLocaleString();
      }
      log.log('LastSuccessfulRefresh', aggregator.lastRefreshSuccessfulAt, lastSuccessfulRefreshOn);
    }

    return (
      <SecondaryTextLine>
        <div>
          {institutionLogin ? `${institutionLogin.name}` : ''}
        </div>
        <div title="refresh-date">
          {lastSuccessfulRefreshOn}
        </div>
      </SecondaryTextLine>
    );
  };

  renderAggError = (institutionLogin, aggStatus, aggStatusCode, classes, depth) => {
    if (institutionLogin != null) {
      if (this.props.isAllInstitutionLoginsRefreshing) {
        return null;
      }
      if (this.props.institutionLoginsIdsRefreshing.has(institutionLogin.id)) {
        return (
          <QIconButton
            disabled
            aria-label="Refreshing"
            classes={{ root: classes.refreshingProgress }}
            size="small"
          >
            <CircularProgress size={20} style={{ color: this.props.theme.palette.blue5 }}/>
          </QIconButton>
        );
      }
      const error = getQcsAggregationError(institutionLogin, aggStatus, aggStatusCode, this.props.featureFlags);
      return (
        <QIconButton
          aria-label="Error"
          classes={{
            root: classes[`listItemIconButtonL${depth}`],
          }}
          onClick={(e) => {e.stopPropagation(); this.handleErrorIconClick(error)}}
          size="small"
        >
          <PriorityHigh classes={{ root: classes.errorIcon }} />
        </QIconButton>
      );
    }
    return null;
  };

  renderGoalLine = (text, amount) => {
    const { classes, node, omitCents } = this.props;
    return (
      <PrimaryTextLine>
        <span
          style={{ width: '100%', paddingLeft: 8 }}
          className={classNames(classes.listItemTextL3, 'left')}
        >
          <QTip
            title={text}
            wrapOnly
          >
            <div
              style={{ width: '100%' }}
              className={classNames(classes.listItemTextL3, 'left')}
            >
              {text}
            </div>
          </QTip>
        </span>
        <AmountField
          id={`${this.props.id}-balance`}
          value={amount}
          classes={{ inputField: classNames(classes.listItemTextL3, amount < 0 ? classes.negativeBalDark : classes.positiveBalDark) }}
          omitCents={omitCents}
          currencySymbol={node.currency}
        />
      </PrimaryTextLine>);
  };

  render() {
    const { selectedNodeId, node, dense = false, displayOptions, onClick, onExpandCollapse,
      classes, omitCents, showColors } = this.props;

    const isLeafNode = node.depth === 2 && node.isLeaf === true;
    let depth = isLeafNode ? 3 : node.depth;
    if (node.depth === 1 && node.displayOrder === 0) {
      depth = 0;
    }

    let aggStatus = null;
    let aggStatusCode = null;
    if (node.account && node.account.aggregators) {
      const aggregator = node.account.aggregators.get(0);

      aggStatus = aggregator.aggStatus;
      aggStatusCode = aggregator.aggStatusCode;
    }

    // const rowClass = displayOptions.selected ? 'selected' : '';
    const nodeHasFixedExpansion = node.depth > MAX_LEVEL_TO_EXPAND_COLLAPSE;
    const nodeIsExpandable = node.children && node.expandable && !nodeHasFixedExpansion;

    const selClass = selectedNodeId === node.id ? 'selected' : '';

    const institutionLogin = !(node.account && node.account.institutionLoginId && this.props.institutionLoginsById) ? null :
      this.props.institutionLoginsById.get(node.account.institutionLoginId);

    const drawerTheme = this.props.theme.components.accountDrawer;

    const UpIcon = isAcme ? ArrowUp : ExpandLess;
    const DownIcon = isAcme ? ArrowDown : ExpandMore;

    const expandable = isAcme ? (node.children && node.expandable && !nodeHasFixedExpansion && node.id !== 'HIDDEN') :
      (node.children && node.expandable && !nodeHasFixedExpansion);

    const displayGoals = isAcme && depth === 3 && Number(node.sumGoalsAccountBalances) !== 0 && !node.children;

    const iconSpace = (
      <ListItemSecondaryAction
        style={(isAcme && expandable) ? { left: drawerTheme.basePadding - 6 } : { right: (drawerTheme.listPaddingRight - drawerTheme.iconSize)/2 }}
      >
        {expandable &&
          <QIconButton
            aria-label="Expand/Collapse"
            classes={{
              root: classes[`listItemIconButtonL${depth}`],
            }}
            onClick={(e) => { e.stopPropagation(); onExpandCollapse(displayOptions) }}
          >
            {displayOptions && displayOptions.expand ? <UpIcon /> : <DownIcon />}
          </QIconButton>
        }
        {aggStatus != null && aggStatus !== 'OK' && this.renderAggError(institutionLogin, aggStatus, aggStatusCode, classes, depth)}
      </ListItemSecondaryAction>
    );

    const primaryText = this.renderPrimaryText(node, classes, omitCents, selClass, iconSpace);

    return (
      <div
        role="list"
      >
        <ListItem
          button
          className={classNames(selClass, nodeIsExpandable && 'expandable', classes[`listItemL${depth}`])}
          onClick={(e) => {
            e.stopPropagation();
            onClick(node);
          }}
          key={`${node.id}`}
          id={this.props.id}
        >
          {node.account && showColors &&
          <div
            className={classes.accountBar}
            style={{ background: this.props.accountColorsById[node.account.id] }}
          />
          }

          <ListItemText
            disableTypography
            primary={primaryText}
            secondary={!dense ? this.renderSecondaryText(node, institutionLogin) : null}
            className={classNames(selClass, classes.listItemText)}
            style={{ width: '100%' }}
          />
        </ListItem>
        {node.children && node.expandable &&
          <Collapse
            classes={{
              container: classes.collapseContainer,
            }}
            in={nodeHasFixedExpansion || (displayOptions && displayOptions.expand)}
            unmountOnExit
          >
            {node.children.map((child, i) => (
              <CSSTransition
                key={child.id}
                transitionAppear={!isIe}
                enterInitStyle={{ opacity: 0, transform: 'translateY(30px)' }}
                enterStyle={{ opacity: 1, transform: 'translateY(0)', transition: 'all 500ms ease', transitionDelay: `${i * 0.05}s` }}
                active
              >
                {!child.hide &&
                <AccountRow
                  {...this.props}
                  id={`${this.props.id}-${child.displayLabel}-${i}`}
                  node={child}
                  displayOptions={
                    displayOptions && displayOptions.children ? displayOptions.children.get(child.id) : null
                  }
                  key={child.id}
                  style={{ transitionDelay: `${i * 0.05}s` }}
                />
                }
              </CSSTransition>
            ))}
          </Collapse>
        }

        {displayGoals &&
          <>
            {/* Savings Goals section */}
            <ListItem
              button
              className={classes.listItemL3}
              onClick={(e) => {
                e.stopPropagation();
                this.props.history.push('/goals');
              }}
              key={`${node.id}-savings-goals`}
              id={`${this.props.id}-savings-goals`}
            >
              <ListItemText
                disableTypography
                primary={this.renderGoalLine("Savings Goals", (0 - node.sumGoalsAccountBalances))}
                className={classes.listItemText}
                style={{ width: '100%' }}
              />
            </ListItem>

            {/* Available Balance section */}
            <ListItem
              button
              className={classes.listItemL3}
              onClick={(e) => {
                e.stopPropagation();
                onClick(node);
              }}
              key={`${node.id}-available-balance`}
              id={`${this.props.id}-available-balance`}
            >
              <ListItemText
                disableTypography
                primary={this.renderGoalLine("Available Balance", (this.getBalance(node) + node.sumGoalsAccountBalances))}
                className={classes.listItemText}
                style={{ width: '100%' }}
              />
            </ListItem>
          </>
        }
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    institutionLoginsById: getInstitutionLoginsById(state),

    isAllInstitutionLoginsRefreshing: getIsAllInstitutionLoginsRefreshing(state),
    institutionLoginsIdsRefreshing: getInstitutionLoginIdsRefreshing(state),

    accountColorsById: preferencesSelectors.accountColorsById(state),

    featureFlags: getFeatureFlags(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
  };
}

export default compose(
  withStyles(styles, { name: 'AccountRow', withTheme: true }),
  connect(mapStateToProps, mapDispatchToProps),
)(withRouter(AccountRow));
